import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ChevronRight } from "lucide-react";
import { getArticle, ARTICLES } from "@/data/articles";
import { CtaBand } from "@/pages/Home";
import Seo from "@/components/Seo";

// Map each article to its most relevant commercial service landing page.
// Used for mid-article contextual CTA and the "Related pages" block.
const ARTICLE_TO_SERVICE = {
  "storefront-signs": { href: "/storefront-signs-richmond-va", label: "Storefront Signs in Richmond, VA" },
  "monument-signs": { href: "/monument-signs-richmond-va", label: "Monument Signs in Richmond, VA" },
  "building-signs": { href: "/commercial-signs-richmond-va", label: "Commercial Signs in Richmond, VA" },
  "window-graphics": { href: "/storefront-signs-richmond-va", label: "Storefront Signs in Richmond, VA" },
  "do-i-need-a-sign-permit": { href: "/commercial-signs-richmond-va", label: "Commercial Signs — Surveys, Permits & Installation" },
  "landlord-rejected-sign-package": { href: "/new-business-signage-richmond-va", label: "New Business Signage in Richmond, VA" },
  "repair-or-replace": { href: "/commercial-signs-richmond-va", label: "Sign Repair & Replacement Support" },
  "what-to-ask-before-signing-a-sign-proposal": { href: "/commercial-signs-richmond-va", label: "Commercial Signs in Richmond, VA" },
  "after-approval-process": { href: "/sign-installation-richmond-va", label: "Sign Installation in Richmond" },
};

function InlineServiceCta({ service }) {
  return (
    <aside className="my-14 border-l-4 border-[#F97316] bg-[#F8F8F5] px-6 py-6 lg:px-8 lg:py-7">
      <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Related Service</div>
      <p className="mt-3 text-lg text-[#1A1A1A] leading-snug font-heading" style={{ fontWeight: 700 }}>
        Read more about {" "}
        <Link to={service.href} className="text-[#F97316] hover:underline">
          {service.label}
        </Link>
        {" "}or send Todd a note to talk through your specific project.
      </p>
    </aside>
  );
}

function Block({ block }) {
  if (block.type === "h2") {
    return <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#1A1A1A] mt-14 mb-5 leading-tight">{block.text}</h2>;
  }
  if (block.type === "p") {
    return <p className="text-lg text-[#2B2F33]/85 leading-[1.75] mb-5">{block.text}</p>;
  }
  if (block.type === "ul") {
    return (
      <ul className="my-6 border-t border-[#1A1A1A]/10">
        {block.items.map((it, i) => (
          <li key={i} className="border-b border-[#1A1A1A]/10 py-4 flex gap-5 items-start">
            <span className="font-heading text-sm text-[#F97316] tracking-widest shrink-0 mt-1.5" style={{ fontWeight: 800 }}>{String(i + 1).padStart(2, "0")}</span>
            <span className="text-base text-[#2B2F33]/90 leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "ol") {
    return (
      <ol className="my-6 border-t border-[#1A1A1A]/10">
        {block.items.map((it, i) => (
          <li key={i} className="border-b border-[#1A1A1A]/10 py-4 flex gap-5 items-start">
            <span className="font-heading text-sm text-[#F97316] tracking-widest shrink-0 mt-1.5" style={{ fontWeight: 800 }}>{String(i + 1).padStart(2, "0")}</span>
            <span className="text-base text-[#2B2F33]/90 leading-relaxed">{it}</span>
          </li>
        ))}
      </ol>
    );
  }
  if (block.type === "cta") {
    return (
      <div className="my-14 bg-[#F8F8F5] border border-[#1A1A1A]/10 p-8 lg:p-12 flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-0.5 bg-[#F97316]" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Have a project?</span>
          </div>
          <div className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1A1A1A]" style={{ fontWeight: 800 }}>Still have a question? Ready to get started?</div>
        </div>
        <Link to="/contact" className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase tracking-wider px-6 py-3.5 inline-flex items-center gap-2 shrink-0 transition-colors" data-testid="article-inline-cta">
          Talk to Todd <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }
  return null;
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <Navigate to="/resources" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.coverImage,
    "url": `https://rvasigns.com/resources/${article.slug}`,
    "articleSection": article.category,
    "author": {
      "@type": "Person",
      "name": "Todd Friedman",
      "url": "https://rvasigns.com/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "RVA Signs",
      "url": "https://rvasigns.com/"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://rvasigns.com/resources/${article.slug}`
    }
  };

  return (
    <div data-testid="article-page">
      <Seo
        path={`/resources/${article.slug}`}
        title={article.title}
        description={article.excerpt}
        image={article.coverImage}
        jsonLd={articleJsonLd}
      />
      <section className="pt-20 pb-12 lg:pt-28 lg:pb-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link to="/resources" className="text-xs tracking-[0.22em] uppercase font-bold text-[#F97316] inline-flex items-center gap-2 hover:gap-3 transition-all" data-testid="article-back">
            <ArrowLeft className="w-3.5 h-3.5" /> All Resources
          </Link>
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316] mt-8">{article.category} &middot; {article.readTime}</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05] mt-5" style={{ fontWeight: 900 }} data-testid="article-headline">
            {article.title}
          </h1>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="aspect-[3/2] img-zoom bg-[#F8F8F5] overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-center"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          {article.body.map((b, i) => {
            const midpoint = Math.floor(article.body.length / 2);
            const service = ARTICLE_TO_SERVICE[article.slug];
            return (
              <div key={i}>
                <Block block={b} />
                {service && i === midpoint && <InlineServiceCta service={service} />}
              </div>
            );
          })}
        </div>
      </section>

      {/* RELATED PAGES — cross-linking to service pages + top article picks */}
      {ARTICLE_TO_SERVICE[article.slug] && (
        <section className="pb-16 lg:pb-24 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <div className="border-t border-[#1A1A1A]/10 pt-10">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Related Pages</div>
              <ul className="mt-5 divide-y divide-[#1A1A1A]/10">
                <li>
                  <Link
                    to={ARTICLE_TO_SERVICE[article.slug].href}
                    className="group flex items-center justify-between gap-4 py-4"
                    data-testid={`article-related-service-${article.slug}`}
                  >
                    <span className="font-heading text-lg text-[#1A1A1A] tracking-tight" style={{ fontWeight: 700 }}>
                      {ARTICLE_TO_SERVICE[article.slug].label}
                    </span>
                    <ChevronRight className="w-5 h-5 text-[#F97316] group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                  </Link>
                </li>
                {related.slice(0, 2).map((a) => (
                  <li key={a.slug}>
                    <Link
                      to={`/resources/${a.slug}`}
                      className="group flex items-center justify-between gap-4 py-4"
                    >
                      <span className="font-heading text-lg text-[#1A1A1A] tracking-tight" style={{ fontWeight: 700 }}>
                        {a.title}
                      </span>
                      <ChevronRight className="w-5 h-5 text-[#F97316] group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/projects"
                    className="group flex items-center justify-between gap-4 py-4"
                  >
                    <span className="font-heading text-lg text-[#1A1A1A] tracking-tight" style={{ fontWeight: 700 }}>
                      Project Examples
                    </span>
                    <ChevronRight className="w-5 h-5 text-[#F97316] group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>
      )}

      <section className="py-20 lg:py-28 bg-[#F8F8F5] border-t border-[#1A1A1A]/8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-8 h-0.5 bg-[#F97316]" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Keep Reading</span>
          </div>
          <h3 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1A1A1A] leading-[1.05] mb-12" style={{ fontWeight: 800 }}>More guides on signage.</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1A1A1A]/8 border border-[#1A1A1A]/8">
            {related.map((a) => (
              <Link key={a.slug} to={`/resources/${a.slug}`} className="bg-white p-8 group flex flex-col hover:bg-[#F8F8F5] transition-colors" data-testid={`related-${a.slug}`}>
                <div className="aspect-[16/10] img-zoom bg-[#F8F8F5] overflow-hidden">
                  <img
                    src={a.coverImage}
                    alt={a.title}
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                </div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#F97316] font-bold mt-5">{a.category}</div>
                <h4 className="font-heading text-xl tracking-tight text-[#1A1A1A] mt-3 leading-snug" style={{ fontWeight: 700 }}>{a.title}</h4>
                <div className="mt-5 text-xs tracking-tight text-[#2B2F33]/60 flex items-center justify-between">
                  <span>{a.readTime}</span>
                  <ArrowUpRight className="w-4 h-4 text-[#1A1A1A] group-hover:text-[#F97316] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
